'use client';
import React, { createContext, useContext, useEffect, useMemo } from 'react';
import type { CmsProduct } from '../../types/cmsProduct';
import {
  MINIMUM_ANNUAL_INCOME,
  NO_VERDICT,
  type EligibilityDetails,
  type EligibilityVerdict,
} from '../../types/eligibility';
import { registerEligibilityOutcomeResolver } from '../../utils/analytics/eligibilityTracking';
import {
  deriveCitizenshipOptions,
  hasIncomeCriteria,
} from './deriveCitizenshipOptions';
import type { FilterableProduct } from './productFilterTypes';
import { resolveEligibility } from './resolveEligibility';
import { useProductEligibility } from './useProductEligibility';

type ProductEligibilityContextValue = {
  details: EligibilityDetails;
  /**
   * The citizenship values this page's products carry, in display order. Empty
   * means the control is not offered — a market only shows the checks its own
   * lender data supports (UC 8).
   */
  citizenshipOptions: readonly string[];
  /** Whether any product publishes a minimum income, gating that control. */
  incomeSupported: boolean;
  /** Whether an income has been entered, which is what turns outcomes on. */
  hasIncome: boolean;
  /** Whether this page offers the checks at all — see the provider. */
  supported: boolean;
  setCitizenship: (value: string | undefined) => void;
  setMonthlyIncome: (value: number | undefined) => void;
  clearDetails: () => void;
  /** One loan's outcome. Cached per product, since both blocks ask. */
  verdictFor: (
    product: FilterableProduct | undefined | null,
  ) => EligibilityVerdict;
};

/**
 * No panel on the page: nothing offered, nothing entered, and every loan
 * resting. A block unit-tested bare gets this rather than throwing.
 */
const EMPTY: ProductEligibilityContextValue = {
  details: {},
  citizenshipOptions: [],
  incomeSupported: false,
  hasIncome: false,
  supported: false,
  setCitizenship: () => undefined,
  setMonthlyIncome: () => undefined,
  clearDetails: () => undefined,
  verdictFor: () => NO_VERDICT,
};

const ProductEligibilityContext =
  createContext<ProductEligibilityContextValue>(EMPTY);

/**
 * The table and the cards are separate blocks in the dynamic zone, so neither
 * can see the panel's inputs alone. Resolved once here and published, for the
 * same reason the sort and filter configs are single: two copies could disagree
 * about whether a loan meets the criteria, and the count above the results
 * would then contradict the loans under it.
 */
export const ProductEligibilityProvider: React.FC<{
  /**
   * The page's products. Omitted where the caller cannot enumerate them, which
   * offers no citizenship options rather than claiming there are none.
   */
  products?: readonly FilterableProduct[];
  children: React.ReactNode;
}> = ({ products, children }) => {
  const citizenshipOptions = useMemo(
    () => deriveCitizenshipOptions(products),
    [products],
  );
  const incomeSupported = useMemo(
    () => hasIncomeCriteria(products, MINIMUM_ANNUAL_INCOME),
    [products],
  );

  const { details, setCitizenship, setMonthlyIncome, clearDetails } =
    useProductEligibility(citizenshipOptions);

  /**
   * Whether this page offers the checks at all. It gates the outcomes as well
   * as the controls: `cms_income` is readable on any page, so without this a
   * link carrying it plays products down on a vertical that never offered the
   * reader a way to see or clear it.
   */
  const supported = citizenshipOptions.length > 0 || incomeSupported;

  const value = useMemo(() => {
    // Both blocks resolve the same products, and the table re-renders on every
    // calculator keystroke: without this the whole catalogue is re-judged each
    // time. Rebuilt whenever the details change, which is when verdicts change.
    const cache = new Map<FilterableProduct, EligibilityVerdict>();

    return {
      details,
      citizenshipOptions,
      incomeSupported,
      hasIncome: supported && !!details.monthlyIncome,
      supported,
      setCitizenship,
      setMonthlyIncome,
      clearDetails,
      verdictFor: (product: FilterableProduct | undefined | null) => {
        if (!product || !supported) return NO_VERDICT;
        const cached = cache.get(product);
        if (cached) return cached;
        const verdict = resolveEligibility(product, details);
        cache.set(product, verdict);
        return verdict;
      },
    };
  }, [
    details,
    citizenshipOptions,
    incomeSupported,
    supported,
    setCitizenship,
    setMonthlyIncome,
    clearDetails,
  ]);

  /**
   * Product events on this page report the outcome as `eo` (CMS-2063). Only
   * where the page offers the check: off it, nothing registers and no event
   * changes.
   */
  const { verdictFor } = value;
  useEffect(() => {
    if (!supported) return undefined;
    return registerEligibilityOutcomeResolver(
      (product: CmsProduct) =>
        verdictFor(product as FilterableProduct).outcome,
    );
  }, [supported, verdictFor]);

  return (
    <ProductEligibilityContext.Provider value={value}>
      {children}
    </ProductEligibilityContext.Provider>
  );
};

export const useProductEligibilityState = (): ProductEligibilityContextValue =>
  useContext(ProductEligibilityContext);
